import Vue from 'vue'
import Axios from 'axios'
import i18n from '@/i18n'
import maintenanceErrorImage from '@/assets/images/broken-robot.svg'
import router from '@/router'
import { NotifyMessage } from '@/service/notification/NotifyMessage'
import ServiceRegistry from '@/service/registry/ServiceRegistry'
import { NotificationService } from '@/service/notification/NotificationService'
import { RequestCodeVersion } from '@/service/order_info/object/RequestCodeVersion'

export const PLATFORM_HASH_HEADER = 'X-PLATFORM-HASH'
export const ANONYMOUS_PLATFORM_HASH = 'P-anonymous'

export const TIMEOUT_ERROR_CODE = 'ECONNABORTED'

export class HttpClientManager {
  #http
  #codeVersion
  #platformHash

  constructor () {
    this.#http = Axios.create({ baseURL: process.env.VUE_APP_API_URL })
    this.#http.interceptors.request.use(this.requestSuccessHandler.bind(this), this.requestFailureHandler.bind(this))
    this.#http.interceptors.response.use(this.responseSuccessHandler.bind(this), this.responseFailureHandler.bind(this))
  }

  requestSuccessHandler (config) {
    config.headers[PLATFORM_HASH_HEADER] = this.#platformHash
    return config
  }

  requestFailureHandler (error) {
    throw error
  }

  responseSuccessHandler (config) {
    return config
  }

  responseFailureHandler (error) {
    // check for timeout error
    if (error?.config?.timeout && error?.code === TIMEOUT_ERROR_CODE) {
      // console.debug('Is timeout error')
    }
    if (error?.response?.status === 503) {
      const errorDetails = {
        errorTitle: i18n.t('maintenance-mode.title'),
        errorBody: i18n.t('maintenance-mode.body'),
        showHelpLink: false,
        errorImage: maintenanceErrorImage
      }
      this.handleHttpError(errorDetails, error, true)
    }

    throw error
  }

  handleHttpError (errorDetails, httpError, redirectToErrorPage = false) {
    if (httpError) {
      console.error(httpError)
      if (Vue.rollbar) {
        Vue.rollbar.log(httpError, { errorDetails })
      }
    }
    const { errorTitle, errorBody, details, timeout } = errorDetails || {}
    if (redirectToErrorPage) {
      router.push({
        name: 'Error',
        params: {
          ...errorDetails,
          allowNavigation: true
        }
      })
    } else {
      ServiceRegistry.inject(NotificationService).push(NotifyMessage.error({
        title: errorTitle,
        message: errorBody,
        details,
        timeout
      }))
    }
  }

  /**
   * @return {string}
   */
  get platformHash () {
    return this.#platformHash
  }

  /**
   * Setter for platform identity hash used in HttpInjector
   * @param {string} hash
   */
  set platformHash (hash) {
    if (hash && typeof hash === 'string') {
      this.#platformHash = hash
    } else {
      this.#platformHash = ANONYMOUS_PLATFORM_HASH
    }
  }

  /**
   * @return {RequestCodeVersion}
   */
  get codeVersion () {
    return this.#codeVersion
  }

  /**
   * Setter for code version object used in HttpInjector
   * @param {RequestCodeVersion} codeVersion
   */
  set codeVersion (codeVersion) {
    if (codeVersion instanceof RequestCodeVersion) {
      this.#codeVersion = codeVersion
    }
  }

  /**
   * Configured Axios instance
   * @return {Axios}
   */
  get http () {
    return this.#http
  }
}

export const HttpClient = new HttpClientManager()
